/**
 * Header scripts.
 *
 * Handles toggling the navigation menu for small screens and enables TAB key
 * navigation support for dropdown menus.
 */

/** Dependencies -------------------------------------------- */

// External

import { throttle } from 'throttle-debounce'

import gsap from 'gsap'
// import ScrollSmoother from 'gsap/ScrollSmoother'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin( ScrollTrigger )

// Design system

import { breakpoints } from '../blocks/style-guide'

import { setupEventName as smoothScrollSetupEventName } from '../smooth-scroll'
import { completionEventName as superheroCompletionEventName } from '../superhero'

import { colors } from '../blocks/style-guide';
import { deriveBackgroundColorContext } from '../blocks/helpers'

import { getCssVar } from '../helpers'

/** The beef ------------------------------------------------ */

/**
 * Header mixin.
 * Provides header functionality to single element.
 */
export let mixin = ( { node = null, config = {} } = {} ) => {

    if ( ! node ) return false

    let state = {
        node,
        config,
		inner               : null,
		facade              : null,
		button              : null,
		items               : null,
		links               : null,
		linksWithChildren   : null,
		backLinks           : null,
		colorTriggers       : null,
		colorScrollTriggers : []
    }

    let module = {

		/** Utilities -------------------------------------- */

		getBottomOffset() {
			return state.node.getBoundingClientRect().bottom
		},

		openNav() {
			node.classList.add( state.config.toggledClass );
			document.documentElement.classList.add( state.config.hasToggledClass )
			state.button.setAttribute( 'aria-expanded', 'true' )
			// ScrollSmoother.get().paused( true )
		},

		closeNav() {
			node.classList.remove( state.config.toggledClass );
			document.documentElement.classList.remove( state.config.hasToggledClass )
			state.button.setAttribute( 'aria-expanded', 'false' )
			Array.from( state.items ).map( ( item ) => item.classList.remove( 'has-focus' ) )
			// ScrollSmoother.get().paused( false )
		},

		/** Animations -------------------------------------- */

		setRollInitialStates() {

			const headerHeight = state.node.clientHeight

			gsap.set( state.node, { perspective: 1993, autoAlpha : 1 } )
			gsap.set( state.inner, { rotateX: 180, z: -headerHeight / 2  } )
			gsap.set( state.facade, { transformOrigin: `center center -${headerHeight / 2}`, z: headerHeight / 2, rotateX: -90 } )
		},

		animateRoll() {
			gsap.to( state.inner, { rotateX: 90, duration: 0.65 } )
		},

		setColorInitialStates() {
			gsap.set( state.facade, { backgroundColor: 'none', paddingTop: getCssVar( 'headerYOffset', state.facade ) } )
		},

		animateColor() {

			// Color-changing magic
			// Contrary to past versions, this one just gradually adds a background as the user scrolls towards the end of the color trigger

			if ( node.classList.contains( state.config.stickyClass ) ) {

				for ( let i = 0; i < state.colorTriggers.length; i++ ) {

					if ( state.colorScrollTriggers[ i ] && state.colorScrollTriggers[ i ].timeline ) {
						state.colorScrollTriggers[ i ].timeline.kill()
						state.colorScrollTriggers[ i ].scrollTrigger.kill()
					}
					else {
						state.colorScrollTriggers[ i ] = {}
					}

					const colorTrigger = state.colorTriggers.item( i )
					const toHex = colors[deriveBackgroundColorContext( colorTrigger ).color].hex

					const timeline = gsap.timeline( { paused: true } )
						.to( state.facade, { backgroundColor: toHex, paddingTop: 0 } )

					const scrollTrigger = ScrollTrigger.create(
						{
							animation   : timeline,
							scrub       : true,
							trigger     : colorTrigger,
							start       : () => `top top`,
							end         : () => `bottom ${this.getBottomOffset() + state.config.colorTriggerOffset}px`,
						}
					)

					state.colorScrollTriggers[ i ].timeline = timeline
					state.colorScrollTriggers[ i ].scrollTrigger = scrollTrigger
				}
			}
		},

		/** Event handlers ---------------------------------- */

		onToggleClick() {

			if ( state.button.getAttribute( 'aria-expanded' ) === 'true' ) {
				this.closeNav()
			}
			
			else {
				this.openNav()
			}
		},

		onDocumentClickOutside( e ) {

			const isClickInside = node.contains( e.target );

			if ( ! isClickInside ) {
				node.classList.remove( 'is-toggled' )
				Array.from( state.items ).map( ( item ) => item.classList.remove( 'has-focus' ) )
				Array.from( state.links ).map( ( link ) => link.classList.remove( 'has-focus' ) )
				document.documentElement.classList.remove( 'has-toggled-nav' )
				state.button.setAttribute( 'aria-expanded', 'false' )
				// ScrollSmoother.get().paused( false )
			}
		},

		onLinkClick( e ) {
			
			const href = e.target.getAttribute( 'href' )
			
			if ( href && href[0] === '#' ) {
				this.closeNav()
			}
		},

		onBackLinkClick( e ) {
			
			let target = e.target;
			
			// Move up through the ancestors of the current link until we hit .nav-menu.
			while ( ! target.classList.contains( 'nav-menu' ) ) {
				
				// Return as soon as we find an item that has focus
				if ( target.classList.contains( 'has-focus' ) ) {
					target.classList.remove( 'has-focus' )
					return
				}
				target = target.parentNode
			}
		},

		toggleFocus( e ) {

			if ( e.type === 'focus' || e.type === 'blur' ) {

				if ( window.innerWidth >= breakpoints[state.config.breakpoint].threshold ) {

					let target = e.target;

					// Move up through the ancestors of the current link until we hit .nav-menu.
					while ( target.classList && ! target.classList.contains( 'nav-menu' ) ) {

						// On li elements toggle the class .focus.
						if ( 'li' === target.tagName.toLowerCase() ) {
							target.classList.toggle( 'has-focus' )
						}
						target = target.parentNode
					}
				}
			}

			if ( e.type === 'click' ) {

				const target = e.target;
				const menuItem = target.parentNode;

				e.preventDefault();

				for ( const link of menuItem.parentNode.children ) {
					if ( menuItem !== link ) {
						link.classList.remove( 'has-focus' );
					}
				}
				menuItem.classList.toggle( 'has-focus' );
			}
		},

		onSuperheroComplete() {
			// this.animateRoll()
			// this.animateColor()
		},

		onSmoothScrollSetup() {
			
			if ( state.button.getAttribute( 'aria-expanded' ) === 'true' ) {
				this.closeNav()
			}
			// this.setRollInitialStates()
			// this.setColorInitialStates()
		},

		onScrollTriggerRefresh() {
			// this.animateColor()
		},

		onResize() {
			// this.setColorInitialStates()
		},
		
		/** Setup ------------------------------------------- */

		setEventBindings() {

			window.addEventListener( smoothScrollSetupEventName, this.onSmoothScrollSetup.bind( this ) )
			window.addEventListener( superheroCompletionEventName, this.onSuperheroComplete.bind( this ) )

			ScrollTrigger.addEventListener( 'refresh', this.onScrollTriggerRefresh.bind( this ) )

			window.addEventListener( 'resize', throttle( state.config.resizeThrottle, this.onResize.bind( this ) ) )

			state.button.addEventListener( 'click', this.onToggleClick.bind( this ) )
			document.addEventListener( 'click', this.onDocumentClickOutside )

			Array.from( state.links ).map(
				( link ) => {
					link.addEventListener( 'click', this.onLinkClick.bind( this ) )
					link.addEventListener( 'focus', this.toggleFocus, true )
					link.addEventListener( 'blur', this.toggleFocus, true )
				}
			)
			
			Array.from( state.linksWithChildren ).map( ( linkWithChildren ) => linkWithChildren.addEventListener( 'click', this.toggleFocus ) )

			Array.from( state.backLinks ).map(
				( backLink ) => {
					backLink.addEventListener( 'click', this.onBackLinkClick )
				}
			)
		},
        
        init() {

			state.inner = node.querySelector( '[data-inner]' )
			state.facade = node.querySelector( '[data-facade]' )
			state.button = node.getElementsByTagName( 'button' )[ 0 ]
			state.items = node.querySelectorAll( '.menu-item' )
			state.links = node.querySelectorAll( 'a:not( .menu-item.menu-item-goes-back > a )' )
			state.linksWithChildren = node.querySelectorAll( '.menu-item-has-children > a, .page_item_has_children > a' )
			state.backLinks = node.querySelectorAll( '.menu-item.menu-item-goes-back > a' )

			state.colorTriggers = document.querySelectorAll( state.config.colorTriggerSelector )

			this.setEventBindings()			
        },
    }

    return module.init()
};

/**
 * Header module.
 * Provides an interface to apply header functionality to all elements matching a selector.
 */
export default {

    init( { selector, config = {} }) {

        let selected = document.querySelectorAll( selector )
        if ( ! selected.length ) return

		const defaultConfig = {
			breakpoint           : 'landscapeTablet',
			stickyClass          : 'is-sticky',
			colorTriggerSelector : 'main > section:first-child',
			colorTriggerOffset   : 0,
			toggledClass         : 'is-toggled',
			hasToggledClass      : 'has-toggled-nav',
			resizeThrottle       : 30
		}

        return Array.from( selected ).map(
			( node, index ) => mixin( { node, config: { ...defaultConfig, ...config } } )
        )
    },
};

